import { z } from 'zod';
import { failure, success, type Result } from '../types.js';
import { isNotificationEvent, notificationCatalog, type NotificationAudience } from './catalog.js';

const id = z.string().trim().min(1);
const ids = z.array(id);
const factsSchema = z
  .object({
    eligible: z.boolean(),
    previewing: z.boolean().optional(),
    requestId: id.optional(),
    line: z
      .object({
        id,
        personId: id,
        visible: z.boolean(),
        removed: z.boolean(),
        hasCash: z.boolean(),
        state: z.enum([
          'S0',
          'S1a',
          'S1b',
          'S2',
          'S3',
          'S4',
          'S5',
          'S6',
          'S7',
          'S7x',
          'S8',
          'S9',
          'S10',
          'S11',
          'X1',
          'X2',
        ]),
      })
      .strict()
      .optional(),
    awardType: z.enum(['SDB', 'EDB', 'PAA', 'TAA', 'SPOT']).optional(),
    sectorLeadId: id.optional(),
    delegateId: id.optional(),
    creatorId: id.optional(),
    requesterId: id.optional(),
    executiveIds: ids.optional(),
    recipientIds: ids.optional(),
    stockAdminIds: ids.optional(),
    /** APP-148 D18: the System Admins, told about a question on something done for someone. */
    systemAdminIds: ids.optional(),
    payrollIds: ids.optional(),
    letterHolderIds: ids.optional(),
    /**
     * APP-238: every letter released (together, or a Spot letter's one line) is held by its own
     * employee, so nobody hands one out (SENT-134).
     */
    lettersSelfHeld: z.boolean().optional(),
    affectedPersonId: id.optional(),
    allEmployeeIds: ids.optional(),
    managerId: id.optional(),
    actingPersonId: id.optional(),
    /** APP-148 D18: the person a Stock Admin acted for, who is told by their own notice. */
    onBehalfOfPersonId: id.optional(),
    delegationChange: z.boolean().optional(),
    /** An election reminder a Stock Admin sent after the deadline (APP-067 Chase). */
    chased: z.boolean().optional(),
  })
  .strict();

export type NotificationScope = {
  readonly kind: 'request' | 'line' | 'person' | 'digest';
  readonly id: string;
};
export interface NotificationPersonTarget {
  readonly personId: string;
  readonly view: 'employee' | 'staff' | 'personal';
  readonly audiences: readonly NotificationAudience[];
  readonly scope: NotificationScope;
  /** Executive summaries use the single group route, never one email per Executive. */
  readonly email: 'direct' | 'digest' | 'none';
  readonly inApp: boolean;
}
export interface NotificationRecipients {
  readonly people: readonly NotificationPersonTarget[];
  /** The manager must resolve the configured address or report an unresolved delivery route. */
  readonly executiveGroupEmail: {
    readonly destination: 'executives';
    readonly scope: NotificationScope;
  } | null;
}
const empty: NotificationRecipients = { people: [], executiveGroupEmail: null };

/**
 * Facts are loaded and authorized by a manager, never copied from a request body. These IDs do
 * not authorize fetching a whole award, its documents, or another person's line for a template.
 */
export function notificationRecipients(
  event: string,
  input: unknown,
): Result<NotificationRecipients> {
  if (!isNotificationEvent(event)) return failure('unknown_notification_event');
  const parsed = factsSchema.safeParse(input);
  if (!parsed.success) return failure('invalid_notification_facts');
  const f = parsed.data;
  if (f.previewing || !f.eligible) return success(empty);
  const definition = notificationCatalog[event];
  if (event === 'self_recognition_approval_needed' && f.recipientIds === undefined)
    return failure('recipient_scope_required');
  const requestEvents = [
    'self_recognition_approval_needed',
    'delegate_submitted',
    'delegate_approval_needed',
    'delegate_decision',
    'executive_sector_award',
    'overage_approval_needed',
    'overage_decision',
    'paa_taa_approval_needed',
    'executive_award_created',
    'requester_confirmation',
    'executive_award_final_summary',
    'stock_admin_queue',
    'executive_approval_handled',
    'check_returned',
    'executive_decision',
    'letter_template_ready',
    'letter_gap',
  ];
  // APP-092: letters released together are announced once per holder (and once to an Executive
  // Discretionary requester), about the request: one notice lists that person's letters. A Spot
  // letter stays bound to its one line, whose holder is checked against protected Spot authority.
  const groupedLetters =
    event === 'letters_released' && f.line === undefined && f.requestId !== undefined;
  const requestEvent = requestEvents.includes(event) || groupedLetters;
  // APP-138: a feedback notice goes to the one person it names (its report's sender), never as a
  // line notice; the feedback delivery names them. APP-148: what was done for someone may have no
  // award (a budget or setting change), so each notice is the person's own, about one action.
  const actedForEvent = ['acted_for_you', 'acted_for_questioned', 'acted_for_settled'].includes(
    event,
  );
  const personalEvent =
    [
      'role_changed',
      'getting_started',
      'go_live_welcome',
      'feedback_replied',
      'feedback_status_changed',
    ].includes(event) || actedForEvent;
  const lineEvent = !requestEvent && !personalEvent && event !== 'stock_admin_digest';
  if (lineEvent && f.line === undefined) return failure('notification_line_required');
  const employeeEvent = definition.audiences.includes('employee');
  if (employeeEvent && f.line === undefined) return failure('notification_line_required');
  if (lineEvent && f.line && (f.line.removed || ['X1', 'X2'].includes(f.line.state)))
    return success(empty);
  if (employeeEvent && !f.line?.visible) return success(empty);
  // A chase is an election reminder sent once the deadline has passed (APP-067): the line is
  // past its deadline, not still open.
  const chasedReminder = event === 'election_reminder' && f.chased === true;
  if (chasedReminder && f.line?.state !== 'S7x') return success(empty);
  if (
    ['election_opened', 'election_reminder'].includes(event) &&
    !chasedReminder &&
    f.line?.state !== 'S7'
  )
    return success(empty);
  if (event === 'payroll_ready' && (f.line?.state !== 'S8' || !f.line.hasCash))
    return success(empty);
  if (event === 'manager_nudge' && f.line?.state !== 'S7x') return success(empty);
  if (event === 'shareworks_ready' && f.line?.state !== 'S10') return success(empty);
  if (event === 'executive_approval_handled' && f.actingPersonId === undefined)
    return failure('notification_actor_required');
  const scope: NotificationScope | undefined = personalEvent
    ? undefined
    : event === 'stock_admin_digest'
      ? { kind: 'digest', id: 'stock_admin' }
      : f.line !== undefined
        ? { kind: 'line', id: f.line.id }
        : f.requestId !== undefined
          ? { kind: 'request', id: f.requestId }
          : undefined;
  // Executive summaries and request decisions always describe their request, even if the
  // caller also supplied one line as context.
  const selectedScope = requestEvents.includes(event)
    ? f.requestId === undefined
      ? undefined
      : { kind: 'request' as const, id: f.requestId }
    : scope;
  if (!personalEvent && selectedScope === undefined) return failure('notification_scope_required');
  const targets = new Map<string, NotificationPersonTarget>();
  let executiveGroupEmail: NotificationRecipients['executiveGroupEmail'] = null;
  for (const audience of definition.audiences) {
    let recipients: readonly string[] | undefined;
    let optional = false;
    switch (audience) {
      case 'sector_lead':
        if (event === 'role_changed' && !f.delegationChange) continue;
        // A Sector Lead who changed their own delegate's access did it themselves (APP-092).
        if (
          event === 'role_changed' &&
          f.sectorLeadId !== undefined &&
          f.sectorLeadId === f.actingPersonId
        )
          continue;
        if (
          (event === 'check_returned' || event === 'executive_decision') &&
          f.delegateId === undefined
        )
          continue;
        // APP-275: an Executive who leads the sector themselves needs no telling.
        if (event === 'executive_sector_award' && f.sectorLeadId === f.creatorId) continue;
        // A delegation whose sector has no Sector Lead any more still tells the delegate.
        optional =
          event === 'executive_sector_award' ||
          event === 'overage_decision' ||
          event === 'check_returned' ||
          event === 'executive_decision' ||
          event === 'role_changed';
        recipients = f.sectorLeadId === undefined ? undefined : [f.sectorLeadId];
        break;
      case 'delegate':
        recipients = f.delegateId === undefined ? undefined : [f.delegateId];
        break;
      case 'creator':
        // APP-148 D17: whoever entered it hears a settlement only when it was returned to them.
        optional = event === 'acted_for_settled';
        recipients = f.creatorId === undefined ? undefined : [f.creatorId];
        break;
      case 'requester':
        if (event === 'letters_released' && f.awardType !== 'EDB') continue;
        recipients = f.requesterId === undefined ? undefined : [f.requesterId];
        break;
      case 'executives':
        recipients = f.executiveIds;
        if (recipients?.length === 0 && f.executiveIds !== undefined) optional = true;
        break;
      case 'other_executives':
        // Not the one who handled it, nor the Executive it was handled for (APP-148 D18).
        recipients = f.executiveIds?.filter(
          (personId) => personId !== f.actingPersonId && personId !== f.onBehalfOfPersonId,
        );
        optional = f.executiveIds !== undefined;
        break;
      case 'stock_admins':
        // APP-148: a question still reaches the System Admins when nobody is a Stock Admin.
        optional = actedForEvent && f.stockAdminIds !== undefined;
        recipients = f.stockAdminIds;
        break;
      case 'system_admins':
        optional = f.systemAdminIds !== undefined;
        recipients = f.systemAdminIds;
        break;
      case 'letter_holders':
        recipients = f.letterHolderIds;
        // A grouped Executive Discretionary release still reaches its requester without a holder,
        // and letters all held by their own employees have nobody to hand them out (APP-238).
        optional = (groupedLetters && f.awardType === 'EDB') || f.lettersSelfHeld === true;
        break;
      case 'employee':
        recipients = f.line === undefined ? undefined : [f.line.personId];
        break;
      case 'payroll':
        recipients = f.payrollIds;
        break;
      case 'affected_person':
        recipients = f.affectedPersonId === undefined ? undefined : [f.affectedPersonId];
        break;
      case 'all_employees':
        recipients = f.allEmployeeIds;
        break;
      case 'manager':
        recipients = f.managerId === undefined ? undefined : [f.managerId];
        break;
    }
    if (recipients === undefined || recipients.length === 0) {
      if (optional) continue;
      return failure('notification_recipient_required', audience);
    }
    for (const personId of recipients) {
      // APP-148 R25: whoever settled a question did it, so isn't told, in any audience.
      if (event === 'acted_for_settled' && personId === f.actingPersonId) continue;
      const personScope = personalEvent
        ? {
            kind: 'person' as const,
            id: event === 'role_changed' ? (f.affectedPersonId ?? personId) : personId,
          }
        : selectedScope;
      if (personScope === undefined) return failure('notification_scope_required');
      const email =
        audience === 'executives' && event !== 'self_recognition_approval_needed'
          ? 'none'
          : audience === 'stock_admins' &&
              (event === 'election_received' || event === 'stock_admin_digest')
            ? 'digest'
            : 'direct';
      // Keep an employee's own confirmation separate from a staff digest if they hold both roles.
      const view =
        audience === 'employee'
          ? 'employee'
          : ['affected_person', 'all_employees'].includes(audience)
            ? 'personal'
            : 'staff';
      const key = JSON.stringify([personId, personScope.kind, personScope.id, view, email]);
      const previous = targets.get(key);
      targets.set(key, {
        personId,
        view,
        audiences: [...new Set([...(previous?.audiences ?? []), audience])],
        scope: personScope,
        email,
        inApp: event !== 'stock_admin_digest',
      });
      if (audience === 'executives' && event !== 'self_recognition_approval_needed')
        executiveGroupEmail = { destination: 'executives', scope: personScope };
    }
  }
  const people = [...targets.values()];
  // One in-app notice per person. Prefer the narrow employee/personal presentation when
  // their staff role also contributes a digest; email intents remain separate.
  const rank = { employee: 0, personal: 1, staff: 2 };
  return success({
    people: people
      .map((target) => ({
        ...target,
        inApp:
          target.inApp &&
          !people.some(
            (other) =>
              other.personId === target.personId &&
              other.inApp &&
              rank[other.view] < rank[target.view],
          ),
      }))
      .sort((a, b) => a.personId.localeCompare(b.personId) || a.email.localeCompare(b.email)),
    executiveGroupEmail,
  });
}
